import React from 'react'
import { Customer } from '../types/customer'
import { Machine } from '../types/machine'
import { User } from '../types/user'
import {
  ActionType,
  CURRENT_MACHINE_LOAD_ERROR,
  MACHINES_LOAD_ERROR,
  MACHINES_LOAD_SUCCESS,
  SET_CURRENT_MACHINE,
  SET_CURRENT_CUSTOMER,
  START_LOAD_MACHINES,
  START_LOAD_USER,
  USER_LOAD_ERROR,
  USER_LOAD_SUCCESS,
  SET_CUPPING_ROASTS,
  UserLoadErrorSubtype,
} from './actions'

export type Dispatch = (action: ActionType) => void
export type State = {
  isLoadingMachines: boolean
  machines: Array<Machine>
  loadMachinesError: boolean
  currentMachineLoadError: boolean
  isLoadingUser: boolean
  user: User | null
  loadUserError: UserLoadErrorSubtype | null
  currentMachine: Machine | null
  currentCustomer: Customer | null
  cuppingRoastIds: Array<string> | null
}
type ProviderProps = { children: React.ReactNode }

const initialState = {
  isLoadingMachines: false,
  machines: [],
  loadMachinesError: false,
  currentMachineLoadError: false,
  isLoadingUser: false,
  user: null,
  loadUserError: null,
  currentMachine: null,
  currentCustomer: null,
  cuppingRoastIds: null,
}

const StateContext = React.createContext<{ state: State; dispatch: Dispatch } | undefined>(undefined)

function reducer(state: State, action: ActionType) {
  switch (action.type) {
    case START_LOAD_MACHINES: {
      return { ...state, isLoadingMachines: true, loadMachinesError: false }
    }
    case MACHINES_LOAD_SUCCESS: {
      return {
        ...state,
        isLoadingMachines: false,
        machines: [...state.machines, ...action.machines],
      }
    }
    case MACHINES_LOAD_ERROR: {
      return { ...state, isLoadingMachines: false, loadMachinesError: true }
    }
    case CURRENT_MACHINE_LOAD_ERROR: {
      return { ...state, isLoadingMachines: false, currentMachineLoadError: true }
    }
    case START_LOAD_USER: {
      return { ...state, isLoadingUser: true }
    }
    case USER_LOAD_SUCCESS: {
      return {
        ...state,
        isLoadingUser: false,
        user: action.user,
      }
    }
    case USER_LOAD_ERROR: {
      return { ...state, isLoadingUser: false, loadUserError: action.error }
    }
    case SET_CURRENT_MACHINE: {
      return { ...state, currentMachine: action.machine }
    }
    case SET_CURRENT_CUSTOMER: {
      return {
        ...state,
        currentCustomer: action.customer,
        machines: [],
        loadMachinesError: false,
        isLoadingMachines: false,
      }
    }
    case SET_CUPPING_ROASTS: {
      return { ...state, cuppingRoastIds: action.roastIds }
    }
    default: {
      throw new Error('Unhandled action type')
    }
  }
}

function ContextProvider({ children }: ProviderProps) {
  const [state, dispatch] = React.useReducer(reducer, initialState)
  const value = { state, dispatch }
  return <StateContext.Provider value={value}>{children}</StateContext.Provider>
}

function useContextHook() {
  const context = React.useContext(StateContext)
  if (context === undefined) {
    throw new Error('useContextHook must be used within a ContextProvider')
  }
  return context
}

export { ContextProvider, useContextHook }
